/**
 * FitChip — compact "Personalized Investor Fit" pill for the AI Advisor header.
 *
 * Shows the user's live Fit score for the property currently in context so
 * every message feels personal. Reuses /api/investor-profile/fit/{deal_id}.
 *
 * Visibility rules:
 *   - Needs a deal_id in the advisor context (no id → nothing shown).
 *   - If the user has no complete profile → shows a subtle "Set fit" nudge
 *     that deep-links to /profile.
 *   - Silent on fetch errors (best-effort personalization, never blocks chat).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { User, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function bandTheme(score) {
  if (score >= 75) {
    return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-900';
  }
  if (score >= 50) {
    return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-900';
  }
  return 'bg-red-100 text-red-700 border-red-200 dark:bg-red-950/60 dark:text-red-300 dark:border-red-900';
}

export default function FitChip({ dealId, refreshKey }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [fit, setFit] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef({ dealId: null, lang: null, refreshKey: null });

  useEffect(() => {
    if (!dealId) {
      setFit(null);
      return;
    }
    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
    const key = { dealId, lang, refreshKey: refreshKey ?? null };
    const prev = lastFetchRef.current;
    if (prev.dealId === key.dealId && prev.lang === key.lang && prev.refreshKey === key.refreshKey) {
      return;
    }
    lastFetchRef.current = key;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${BACKEND_URL}/api/investor-profile/fit/${dealId}`, {
        withCredentials: true,
        headers: { 'Accept-Language': lang },
      })
      .then((r) => {
        if (!cancelled) setFit(r.data);
      })
      .catch(() => {
        if (!cancelled) setFit(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dealId, i18n.language, refreshKey]);

  if (!dealId) return null;
  if (loading && !fit) return null;
  if (!fit) return null;

  // No profile yet → nudge chip
  if (!fit.has_profile || fit.insufficient_profile) {
    return (
      <button
        type="button"
        data-testid="ai-chat-fit-chip-empty"
        onClick={() => navigate('/profile')}
        title={t('aiAdvisor.fitChip.setupTooltip')}
        className="flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap mr-1 border border-dashed border-[#45C04A]/50 bg-[#45C04A]/10 text-[#12355B] dark:text-[#65D95A] hover:bg-[#45C04A]/20 transition-colors"
      >
        <Sparkles className="w-3 h-3" />
        <span>{t('aiAdvisor.fitChip.setup')}</span>
      </button>
    );
  }

  const score = fit.score ?? 0;
  const label = fit.band || t('aiAdvisor.fitChip.fitLabel');
  const tooltip = t('aiAdvisor.fitChip.tooltip', {
    score,
    band: label,
  });

  return (
    <button
      type="button"
      data-testid="ai-chat-fit-chip"
      onClick={() => navigate('/profile')}
      title={tooltip}
      className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full whitespace-nowrap mr-1 border transition-colors ${bandTheme(score)} hover:opacity-90`}
    >
      <User className="w-3 h-3" />
      <span data-testid="ai-chat-fit-chip-score">
        {t('aiAdvisor.fitChip.fitLabel')} {score}
      </span>
    </button>
  );
}
